/**
 * Email + password flows for the auth gateway (replaces cognitoDirectAuth.ts).
 * Each maps one-to-one onto a Better Auth endpoint; errors surface as
 * AuthApiError and are turned into copy by authErrors.ts.
 */

import {
  resetPasswordWithOtp,
  sendVerificationOtp,
  signInEmail,
  signUpEmail,
  verifyEmailOtp,
} from './authApi'
import { establishSession } from './authSession'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

/** Sign in and mint the first API token. */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const session = await signInEmail({ email, password })
  const outcome = await establishSession(session)
  logger.logEvent(LogCategory.AUTH_LOGIN, 'signin.password.success', { tokenOutcome: outcome })
}

/**
 * Create an account; the server emails a 6-digit code. The gateway has no name
 * field, so (as with Cognito) the email's local part stands in until onboarding
 * asks for a real one.
 */
export async function signUp(email: string, password: string): Promise<void> {
  const name = email.split('@')[0] || email
  await signUpEmail({ email, password, name })
  logger.logEvent(LogCategory.AUTH, 'signup.success')
}

/** Verify the sign-up code. The server signs the user in on success. */
export async function verifyEmail(email: string, otp: string): Promise<void> {
  const session = await verifyEmailOtp({ email, otp })
  const outcome = await establishSession(session)
  logger.logEvent(LogCategory.AUTH, 'verify_email.success', { tokenOutcome: outcome })
}

export async function resendVerificationCode(email: string): Promise<void> {
  await sendVerificationOtp({ email, type: 'email-verification' })
  logger.logEvent(LogCategory.AUTH, 'verify_email.code_resent')
}

/** Start a password reset: emails a 6-digit code. */
export async function requestPasswordReset(email: string): Promise<void> {
  await sendVerificationOtp({ email, type: 'forget-password' })
  logger.logEvent(LogCategory.AUTH, 'forgot_password.code_sent')
}

/** Set the new password with the emailed code. Does not sign in. */
export async function resetPassword(email: string, otp: string, password: string): Promise<void> {
  await resetPasswordWithOtp({ email, otp, password })
  logger.logEvent(LogCategory.AUTH, 'forgot_password.reset_success')
}
